import { useEffect, useRef } from 'react';

import { useAdminDiscourseState, useAdminDiscourseActions } from './AdminDiscourseContext';

import { getDocumentsForAdmin } from './restApi';

const AdminDiscourseLoader = () => {
  const state = useAdminDiscourseState();
  const { initializeState, onResponse, onError } = useAdminDiscourseActions();
  const initializedRef = useRef(false);
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      initializeState();
    }
  }, []);

  useEffect(() => {
    if (!state?.viewState || fetchingRef.current) return;

    const documents = state?.documentsByViewState?.[state?.viewState] || [];
    if (documents.length === 0 && (state?.moreDocumentsAvailableByViewState?.[state?.viewState] ?? true)) {
      fetchingRef.current = true;
      getDocumentsForAdmin({
        viewState: state?.viewState || 'published',
        pageNumber: (state?.pageNumberByViewState?.[state?.viewState] || 0) + 1,
        onResponse: (data) => {
          fetchingRef.current = false;
          onResponse(data);
        },
        onError: (feedback) => {
          fetchingRef.current = false;
          onError(feedback);
        },
      });
    }
  // state?.documentsByViewState is included as a dependency for the initial load
  // sequence where viewState doesn't change but document data goes from empty to
  // populated:
  //
  // 1. Component mounts, state is {} (empty object from AppContext)
  // 2. First effect runs initializeState(), which sets viewState to 'published'
  // 3. viewState goes from undefined → 'published', so the second effect fires
  // 4. documents is empty, so it calls getDocumentsForAdmin
  // 5. The fetch completes, onResponse appends documents to documentsByViewState.published
  // 6. viewState is still 'published' — it hasn't changed
  //
  // At step 6, without documentsByViewState in the dependency array, the effect
  // wouldn't re-run. That's actually fine in the happy path — we don't want it
  // to re-run since documents already loaded. But the effect's closure from step 3
  // still has the old empty documents. If anything else causes a re-render and
  // React decides to re-run the effect (e.g., strict mode), it would see stale
  // data and fetch again.
  //
  // So honestly — it's mostly defensive. The fetchingRef is doing the heavy
  // lifting to prevent duplicate fetches. This dependency just ensures the
  // effect's closure has fresh data if it does re-run, making the effect more
  // correct in terms of React's mental model — your dependencies should reflect
  // what the effect actually reads.
  }, [state?.viewState, state?.documentsByViewState]);

  return null;
};

export default AdminDiscourseLoader;
